import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { SlidePanel } from "@/components/ui/slide-panel";
import { Button } from "@/components/ui/button";
import { StageMovePicker, type StageMoveTarget } from "@/components/pipeline/StageMovePicker";
import { usePipelineStageOptions } from "@/hooks/usePipelineStageOptions";
import { describeMoveTarget, type PickerPipeline, type PlaceNames } from "@/lib/stageMovePicker";
import { useToast } from "@/components/ui/use-toast";
import { getProjectManagerName, NO_PM_MESSAGE, sendStageMoveRequest } from "@/services/stageMoveRequestService";
import { ExtendedProject } from "@/types/project";
import { Stage } from "@/types/pipeline";

// ============================================================================
// Types
// ============================================================================

interface ProjectStagePanelProps {
  isOpen: boolean;
  onClose: () => void;
  project: ExtendedProject | null;
  stages: Stage[];
  /** A stage in the project's own pipeline; keeps that path's document checks. */
  onStageChange: (stageId: number) => Promise<void>;
  onMoveToPipelineStage: (pipelineId: number, stageId: number) => Promise<void>;
  /**
   * PMV-1.6. For users who may not move directly (`canMoveStageDirectly`): the
   * same picker, but the button sends the PM a move request instead of moving.
   */
  requestOnly?: boolean;
}

function placeNames(pipelines: PickerPipeline[], pipelineId: number, stageId: number): PlaceNames {
  const pipeline = pipelines.find((p) => p.id === pipelineId);
  const stage = pipeline?.stages.find((s) => s.id === stageId);
  return { pipeline: pipeline?.name ?? "Unknown Pipeline", stage: stage?.name ?? "Unknown Stage" };
}

// ============================================================================
// Component
// ============================================================================

export function ProjectStagePanel({
  isOpen,
  onClose,
  project,
  stages,
  onStageChange,
  onMoveToPipelineStage,
  requestOnly = false,
}: ProjectStagePanelProps) {
  const [selected, setSelected] = useState<StageMoveTarget | null>(null);
  const [moving, setMoving] = useState(false);
  const [pmName, setPmName] = useState<string | null>(null);
  const { toast } = useToast();
  const pmId = project?.assigned_internal_pm_id ?? null;

  useEffect(() => {
    if (!isOpen || !requestOnly || !pmId) return;
    let cancelled = false;
    getProjectManagerName(pmId)
      .then((name) => { if (!cancelled) setPmName(name); })
      .catch(() => { if (!cancelled) setPmName("Project Manager"); });
    return () => { cancelled = true; };
  }, [isOpen, requestOnly, pmId]);
  const { data: options, isLoading } = usePipelineStageOptions(project?.pipeline_id, isOpen);

  const pipelines: PickerPipeline[] = useMemo(() => {
    if (options?.length) return options;
    if (!project?.pipeline_id) return [];
    return [{ id: project.pipeline_id, name: "Current pipeline", stages }];
  }, [options, project?.pipeline_id, stages]);

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  const from =
    project?.pipeline_id && project.current_stage_id
      ? describeMoveTarget(pipelines, project.pipeline_id, project.current_stage_id)
      : null;
  const to = selected ? describeMoveTarget(pipelines, selected.pipelineId, selected.stageId) : null;
  const isPipelineChange = !!selected && selected.pipelineId !== project?.pipeline_id;

  const handleMove = async () => {
    if (!selected) return;
    setMoving(true);
    try {
      if (isPipelineChange) await onMoveToPipelineStage(selected.pipelineId, selected.stageId);
      else await onStageChange(selected.stageId);
      handleClose();
    } finally {
      setMoving(false);
    }
  };

  const handleRequest = async () => {
    if (!selected || !project || !pmId || !project.pipeline_id || !project.current_stage_id) return;
    setMoving(true);
    try {
      await sendStageMoveRequest({
        pmId,
        projectId: project.id,
        projectName: project.project_name || "Unknown Project",
        from: placeNames(pipelines, project.pipeline_id, project.current_stage_id),
        to: placeNames(pipelines, selected.pipelineId, selected.stageId),
      });
      toast({
        title: "Request Submitted",
        description: `${isPipelineChange ? "Pipeline" : "Stage"} change request sent to ${pmName ?? "Project Manager"}`,
      });
      handleClose();
    } catch (error) {
      console.error("Error creating stage change request:", error);
      toast({ title: "Error", description: "Failed to submit stage change request", variant: "destructive" });
    } finally {
      setMoving(false);
    }
  };

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={requestOnly ? "Request a Move" : "Move Project"}
      width="md"
      position="right"
      showBackdrop={true}
      closeOnBackdropClick={true}
      topOffset="calc(4rem + 10px)"
      panelClassName="!z-[220] border-l-2 border-t-2 border-border rounded-tl-lg"
      backdropClassName="!left-0 z-[100]"
      immediateBackdrop={true}
      showCloseButton={true}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto pb-4">
          {requestOnly && (
            <p className="mb-3 text-sm text-muted-foreground">
              {pmId
                ? `Pick where the project should go. ${pmName ?? "The Project Manager"} gets a task to move it.`
                : NO_PM_MESSAGE}
            </p>
          )}
          {isOpen && (
            <StageMovePicker
              key={project?.id}
              pipelines={pipelines}
              currentPipelineId={project?.pipeline_id}
              currentStageId={project?.current_stage_id}
              selected={selected}
              onSelect={setSelected}
              loading={isLoading}
            />
          )}
        </div>

        {selected && to && (
          <div className="border-t border-border pt-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {from && <span className="rounded-md bg-muted px-2 py-1">{from}</span>}
              <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="rounded-md bg-primary-light px-2 py-1 font-semibold">{to}</span>
            </div>
            {isPipelineChange && (
              <p className="text-xs text-muted-foreground">
                Different pipeline: its checklists and automations apply from the new stage.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelected(null)} disabled={moving}>
                Cancel
              </Button>
              <Button
                onClick={requestOnly ? handleRequest : handleMove}
                disabled={moving || (requestOnly && !pmId)}
                className="gap-2"
              >
                {moving && <Loader2 className="h-4 w-4 animate-spin" />}
                {requestOnly ? "Send request" : "Move project"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </SlidePanel>
  );
}

ProjectStagePanel.displayName = "ProjectStagePanel";
